/**
 * 叠加关系 — which investment programs can be recommended together.
 *
 * Read-only and built purely from stored data: each program's `mutuallyExclusive`
 * lists, as returned by `ListInvestmentPrograms` (see utils/stacking for the exact
 * rule). No new API: it is the same load as the 项目参数 tab.
 *
 * Every program is both a row and a column, in priority order. Hovering a cell
 * highlights its row and column and names which list(s) made the pair exclusive.
 */
import { type CSSProperties, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Multiselect,
  type MultiselectProps,
  SpaceBetween,
  Spinner,
  TextFilter,
} from '@amzn/awsui-components-react';
import {
  colorBackgroundStatusInfo,
  colorBackgroundStatusSuccess,
  colorBackgroundStatusWarning,
  colorBorderDividerDefault,
  colorBackgroundContainerContent,
  colorBackgroundDropdownItemHover,
  colorTextStatusInactive,
  colorTextStatusInfo,
  colorTextStatusSuccess,
  colorTextStatusWarning,
} from '@amzn/awsui-design-tokens/polaris';
import { useTranslation } from 'react-i18next';

import type { InvestmentProgramData } from '../../client/CpaClient';
import { usePrograms } from '../../state/usePrograms';
import { buildStackingMatrix, countRelations, type Relation, type StackingCell } from '../../utils/stacking';
import './StackingPage.css';

/** Design tokens handed to StackingPage.css (Cloudscape's own custom-property names are hashed). */
const TOKEN_VARS = {
  '--cpa-ok-text': colorTextStatusSuccess,
  '--cpa-ok-bg': colorBackgroundStatusSuccess,
  '--cpa-cn-text': colorTextStatusInfo,
  '--cpa-cn-bg': colorBackgroundStatusInfo,
  '--cpa-gl-text': colorTextStatusWarning,
  '--cpa-gl-bg': colorBackgroundStatusWarning,
  '--cpa-muted': colorTextStatusInactive,
  '--cpa-divider': colorBorderDividerDefault,
  '--cpa-surface': colorBackgroundContainerContent,
  '--cpa-hover': colorBackgroundDropdownItemHover,
} as CSSProperties;

/** Glyph per relation. Each is distinct by shape/text, so colour is never the only cue. */
const GLYPH: Record<Relation, string> = {
  stackable: '✓',
  exclusive: '✕',
  china_only: 'CN',
  global_only: 'GL',
  self: '-',
};

const LABEL_KEY = {
  stackable: 'stackingStackable',
  exclusive: 'stackingExclusive',
  china_only: 'stackingChinaOnly',
  global_only: 'stackingGlobalOnly',
  self: 'stackingSelf',
} as const;

const PAIR_RELATIONS = ['stackable', 'exclusive', 'china_only', 'global_only'] as const;

export default function StackingPage() {
  const { t } = useTranslation('cpa');
  const { state, reload } = usePrograms();
  const [filterText, setFilterText] = useState('');
  const [relations, setRelations] = useState<readonly MultiselectProps.Option[]>([]);
  const [hover, setHover] = useState<{ row: string; col: string } | null>(null);

  const programs = useMemo(() => (state.kind === 'ready' ? state.programs : []), [state]);
  const matrix = useMemo(() => buildStackingMatrix(programs), [programs]);
  const counts = useMemo(() => countRelations(matrix), [matrix]);

  const needle = filterText.trim().toLowerCase();
  const visible = matrix.programs.filter((p) => (p.name ?? '').toLowerCase().includes(needle));
  const shownRelations = new Set(relations.map((o) => o.value));
  // A relation filter dims the other cells rather than removing them, so the grid keeps its shape.
  const isDimmed = (relation: Relation) =>
    shownRelations.size > 0 && relation !== 'self' && !shownRelations.has(relation);

  const presentRelations = PAIR_RELATIONS.filter((r) => counts[r] > 0);

  const describe = (row: InvestmentProgramData, col: InvestmentProgramData, cell: StackingCell) =>
    [
      `${row.name} × ${col.name}：${t(LABEL_KEY[cell.relation])}`,
      ...cell.sources.map((s) =>
        t('stackingListedBy', {
          region: t(s.region === 'global' ? 'stackingRegionGlobal' : 'stackingRegionChina'),
          names: s.listedBy.join(' / '),
        }),
      ),
    ].join('\n');

  if (state.kind === 'denied') {
    return <Alert type="warning">{t('programsDenied')}</Alert>;
  }
  if (state.kind === 'error') {
    return (
      <Alert
        type="error"
        header={t('programsLoadError')}
        action={<Button onClick={reload}>{t('programsRetry')}</Button>}
      >
        {state.message}
      </Alert>
    );
  }

  return (
    <div className="cpa-stacking" style={TOKEN_VARS}>
      <Container
        header={
          <Header
            variant="h2"
            counter={state.kind === 'ready' ? `(${matrix.programs.length})` : undefined}
            description={t('stackingCaption')}
          >
            {t('tabStacking')}
          </Header>
        }
      >
        <SpaceBetween size="m">
          <div className="cpa-stacking-filters">
            <div className="cpa-stacking-search">
              <TextFilter
                filteringText={filterText}
                filteringPlaceholder={t('stackingSearch')}
                onChange={({ detail }) => setFilterText(detail.filteringText)}
              />
            </div>
            <div className="cpa-stacking-relation-filter">
              <Multiselect
                selectedOptions={relations}
                onChange={({ detail }) => setRelations(detail.selectedOptions)}
                options={presentRelations.map((r) => ({ value: r, label: t(LABEL_KEY[r]) }))}
                placeholder={t('stackingRelationFilterPlaceholder')}
                ariaLabel={t('stackingRelationFilter')}
                hideTokens
              />
            </div>
          </div>

          {state.kind === 'loading' ? (
            <Box textAlign="center" padding="l">
              <Spinner size="large" />
            </Box>
          ) : visible.length === 0 ? (
            <Box textAlign="center" color="text-body-secondary" padding="l">
              {t('stackingNoMatch')}
            </Box>
          ) : (
            <>
              <div className="cpa-stacking-legend" role="list">
                {presentRelations.map((r) => (
                  <span key={r} className="cpa-stacking-legend-item" role="listitem">
                    <span className={`cpa-stacking-glyph cpa-stacking-${r}`}>{GLYPH[r]}</span>
                    {t(LABEL_KEY[r])}
                    <Box variant="span" color="text-body-secondary" fontSize="body-s">
                      {t('stackingPairs', { count: counts[r] })}
                    </Box>
                  </span>
                ))}
              </div>

              <div className="cpa-stacking-scroll">
                <table className="cpa-stacking-matrix" onMouseLeave={() => setHover(null)}>
                  <thead>
                    <tr>
                      <th className="cpa-stacking-corner" />
                      {visible.map((col) => (
                        <th
                          key={col.programId}
                          scope="col"
                          className={hover?.col === col.programId ? 'is-hover' : undefined}
                        >
                          <span className="cpa-stacking-col-label">{col.name}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => (
                      <tr key={row.programId}>
                        <th scope="row" className={hover?.row === row.programId ? 'is-hover' : undefined}>
                          {row.name}
                        </th>
                        {visible.map((col) => {
                          const cell = matrix.cell(row, col);
                          const text = describe(row, col, cell);
                          const crossed =
                            hover && (hover.row === row.programId || hover.col === col.programId);
                          return (
                            <td
                              key={col.programId}
                              title={text}
                              aria-label={text}
                              className={[
                                `cpa-stacking-${cell.relation}`,
                                crossed ? 'is-cross' : '',
                                isDimmed(cell.relation) ? 'is-dim' : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              onMouseEnter={() => setHover({ row: row.programId!, col: col.programId! })}
                            >
                              <span className="cpa-stacking-glyph">{GLYPH[cell.relation]}</span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </SpaceBetween>
      </Container>
    </div>
  );
}
